import axios from 'axios'
import store from '@/store'

// 开发时代理到本地后端，生产时指向 Railway 后端
const BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://family-feast-production.up.railway.app/api'
  : '/api'

const http = axios.create({
  baseURL: BASE_URL,
  timeout: 10000
})

// 请求拦截器：自动附加 token
http.interceptors.request.use(config => {
  const token = store.state.auth.token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 响应拦截器：处理 401（token 过期）
http.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      store.dispatch('auth/logout')
      // 避免在登录页触发循环跳转
      if (window.location.hash !== '#/login') {
        import('@/router').then(({ default: router }) => {
          router.push({ name: 'Login' })
        })
        store.dispatch('showToast', '登录已过期，请重新登录')
      }
    }
    return Promise.reject(error)
  }
)

// ===== 认证 API =====
export const authApi = {
  register(data) {
    return http.post('/auth/register', data).then(r => r.data.data)
  },
  login(data) {
    return http.post('/auth/login', data).then(r => r.data.data)
  },
  me() {
    return http.get('/auth/me').then(r => r.data.data)
  },
  recoverUsername(birthday) {
    return http.post('/auth/recover-username', { birthday }).then(r => r.data.data)
  },
  resetPassword(data) {
    return http.post('/auth/reset-password', data).then(r => r.data.data)
  }
}

// ===== 用户管理 API =====
export const usersApi = {
  getAll() {
    return http.get('/users').then(r => r.data.data)
  },
  create(data) {
    return http.post('/users', data).then(r => r.data.data)
  },
  update(id, data) {
    return http.put(`/users/${id}`, data).then(r => r.data.data)
  },
  remove(id) {
    return http.delete(`/users/${id}`).then(r => r.data)
  },
  updateRole(id, role) {
    return http.patch(`/users/${id}/role`, { role }).then(r => r.data.data)
  },
  transferSuper(targetId) {
    return http.post('/users/transfer-super', { targetId }).then(r => r.data.data)
  }
}

// ===== 菜品 API =====
export const recipesApi = {
  getAll(params = {}) {
    return http.get('/recipes', { params }).then(r => r.data.data)
  },
  create(data) {
    return http.post('/recipes', data).then(r => r.data.data)
  },
  update(id, data) {
    return http.put(`/recipes/${id}`, data).then(r => r.data.data)
  },
  remove(id) {
    return http.delete(`/recipes/${id}`).then(r => r.data)
  }
}

// ===== 订单 API =====
export const ordersApi = {
  getHistory() {
    return http.get('/orders').then(r => r.data.data)
  },
  getPending() {
    return http.get('/orders/pending').then(r => r.data.data)
  },
  submit(payload) {
    return http.post('/orders', payload).then(r => r.data.data)
  },
  complete(id) {
    return http.patch(`/orders/${id}/complete`).then(r => r.data.data)
  },
  remove(id) {
    return http.delete(`/orders/${id}`).then(r => r.data)
  }
}

// ===== 菜品申请 API =====
export const recipeRequestsApi = {
  submit(data) {
    return http.post('/recipe-requests', data).then(r => r.data.data)
  },
  getAll(params = {}) {
    return http.get('/recipe-requests', { params }).then(r => r.data.data)
  },
  approve(id) {
    return http.patch(`/recipe-requests/${id}/approve`).then(r => r.data.data)
  },
  reject(id, reason = '') {
    return http.patch(`/recipe-requests/${id}/reject`, { reason }).then(r => r.data.data)
  }
}

// ===== 消息 API =====
export const messagesApi = {
  getList(params = {}) {
    return http.get('/messages', { params }).then(r => r.data.data)
  },
  getUnreadCount() {
    return http.get('/messages/unread-count').then(r => r.data.data)
  },
  markRead(payload) {
    return http.patch('/messages/read', payload).then(r => r.data.data)
  },
  deleteMessages(payload) {
    return http.delete('/messages', { data: payload }).then(r => r.data.data)
  },
  broadcast(data) {
    return http.post('/messages/broadcast', data).then(r => r.data.data)
  }
}

// ===== 食材库 API =====
export const ingredientsApi = {
  getList(q) {
    return http.get('/ingredients', { params: { q } }).then(r => r.data.data)
  },
  add(data) {
    return http.post('/ingredients', data).then(r => r.data.data)
  },
  update(id, data) {
    return http.put(`/ingredients/${id}`, data).then(r => r.data.data)
  },
  remove(id) {
    return http.delete(`/ingredients/${id}`).then(r => r.data)
  },
  clear() {
    return http.delete('/ingredients').then(r => r.data)
  }
}

// ===== 菜品食材关联 API =====
export const recipeIngredientsApi = {
  get(recipeId) {
    return http.get(`/recipes/${recipeId}/ingredients`).then(r => r.data.data)
  },
  update(recipeId, ingredients) {
    return http.put(`/recipes/${recipeId}/ingredients`, { ingredients }).then(r => r.data.data)
  }
}

// ===== 菜品选项 API =====
export const recipeOptionsApi = {
  get(recipeId) {
    return http.get(`/recipes/${recipeId}/options`).then(r => r.data.data)
  },
  update(recipeId, groups) {
    return http.put(`/recipes/${recipeId}/options`, { groups }).then(r => r.data.data)
  }
}

// ===== 购物清单 API =====
export const shoppingListApi = {
  generate() {
    return http.get('/shopping-list').then(r => r.data.data)
  },
  generateWeekly() {
    return http.get('/shopping-list/weekly').then(r => r.data.data)
  }
}

// ===== 收藏 API =====
export const favoritesApi = {
  getList() {
    return http.get('/favorites').then(r => r.data.data)
  },
  toggle(recipeId) {
    return http.post(`/favorites/${recipeId}`).then(r => r.data.data)
  }
}

// ===== 数据统计 API =====
export const statsApi = {
  overview(params = {}) {
    return http.get('/stats/overview', { params }).then(r => r.data.data)
  },
  popularRecipes(params = {}) {
    return http.get('/stats/popular-recipes', { params }).then(r => r.data.data)
  },
  monthlyTrend() {
    return http.get('/stats/monthly-trend').then(r => r.data.data)
  },
  categoryBreakdown(params = {}) {
    return http.get('/stats/category-breakdown', { params }).then(r => r.data.data)
  },
  ingredientUsage(params = {}) {
    return http.get('/stats/ingredient-usage', { params }).then(r => r.data.data)
  }
}

// ===== 订单复用 API =====
export const reorderApi = {
  preview(orderId) {
    return http.post(`/orders/${orderId}/reorder`).then(r => r.data.data)
  }
}
